import { z } from "zod";
import { jobIdSchema } from "./job-schemas";

export const MESSAGE_THREAD_PAGE_LIMIT = 25;
export const THREAD_MESSAGE_PAGE_LIMIT = 50;
export const MESSAGE_BODY_MAX_UTF8_BYTES = 4_000;

export const messageThreadIdSchema = jobIdSchema;
export const messageIdSchema = jobIdSchema;
export const messageParticipantRoleSchema = z.enum(["CUSTOMER", "PROVIDER"]);
export const messageJobStatusSchema = z.enum(["OPEN", "BOOKED"]);
export const messageApplicationStatusSchema = z.enum(["PENDING", "CONFIRMED", "NOT_SELECTED"]);

const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1);
const sequenceNoSchema = z.number().int().min(1);
const lastSequenceNoSchema = z.number().int().min(0);
const unreadCountSchema = z.number().int().min(0);
const base64UrlAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function isCanonicalBase64Url(value: string): boolean {
  const remainder = value.length % 4;
  if (remainder === 1) return false;
  if (remainder === 0) return true;

  const finalCharacter = value.at(-1);
  if (finalCharacter === undefined) return false;
  const finalValue = base64UrlAlphabet.indexOf(finalCharacter);
  if (finalValue < 0) return false;
  return remainder === 2 ? (finalValue & 0b001111) === 0 : (finalValue & 0b000011) === 0;
}

export const messageCursorSchema = z
  .string()
  .min(1)
  .max(384)
  .regex(/^[A-Za-z0-9_-]+$/u, "Cursor must use base64url characters")
  .refine(isCanonicalBase64Url, "Cursor must use canonical base64url encoding");

export const messageIdempotencyKeySchema = z
  .string()
  .min(1)
  .max(256)
  .refine((value) => value === value.trim(), "Idempotency key must not have outer whitespace")
  .regex(/^[\x21-\x7e]+$/u, "Idempotency key must use visible ASCII characters");

export function messageUtf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

const canonicalMessageBodyTextSchema = z
  .string()
  .min(1, "Enter a message")
  .refine((value) => value === value.trim(), "Message must not have outer whitespace")
  .refine((value) => !value.includes("\0"), "Message must not contain NUL")
  .refine(
    (value) => messageUtf8ByteLength(value) <= MESSAGE_BODY_MAX_UTF8_BYTES,
    `Use no more than ${MESSAGE_BODY_MAX_UTF8_BYTES} UTF-8 bytes`,
  );

export const messageBodyTextSchema = z
  .string()
  .refine((value) => !value.includes("\0"), "Message must not contain NUL")
  .transform((value) => value.trim())
  .pipe(canonicalMessageBodyTextSchema);

export const messageThreadSchema = z
  .strictObject({
    id: messageThreadIdSchema,
    jobId: jobIdSchema,
    applicationId: jobIdSchema,
    participantRole: messageParticipantRoleSchema,
    jobStatus: messageJobStatusSchema,
    applicationStatus: messageApplicationStatusSchema,
    writable: z.boolean(),
    createdAt: isoDateTimeSchema,
    activityAt: isoDateTimeSchema,
    lastSequenceNo: lastSequenceNoSchema,
    lastReadSequenceNo: lastSequenceNoSchema,
    unreadCount: unreadCountSchema,
    versionNo: versionNoSchema,
  })
  .superRefine((thread, context) => {
    if (Date.parse(thread.activityAt) < Date.parse(thread.createdAt)) {
      context.addIssue({
        code: "custom",
        path: ["activityAt"],
        message: "Thread activity must not precede creation",
      });
    }
    if (thread.lastReadSequenceNo > thread.lastSequenceNo) {
      context.addIssue({
        code: "custom",
        path: ["lastReadSequenceNo"],
        message: "Read sequence must not exceed the latest sequence",
      });
    }
    if (thread.unreadCount > thread.lastSequenceNo - thread.lastReadSequenceNo) {
      context.addIssue({
        code: "custom",
        path: ["unreadCount"],
        message: "Unread count must fit after the read sequence",
      });
    }
    if (thread.applicationStatus === "NOT_SELECTED" && thread.writable) {
      context.addIssue({
        code: "custom",
        path: ["writable"],
        message: "A not-selected application thread must be read-only",
      });
    }
  });

export const threadMessageSchema = z.strictObject({
  id: messageIdSchema,
  threadId: messageThreadIdSchema,
  sequenceNo: sequenceNoSchema,
  senderRole: messageParticipantRoleSchema,
  bodyText: canonicalMessageBodyTextSchema,
  replyToMessageId: messageIdSchema.nullable(),
  sentAt: isoDateTimeSchema,
  versionNo: versionNoSchema,
});

export const messageThreadListResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    threads: z.array(messageThreadSchema).max(MESSAGE_THREAD_PAGE_LIMIT),
    nextCursor: messageCursorSchema.nullable(),
  })
  .superRefine((response, context) => {
    const ids = new Set<string>();
    response.threads.forEach((thread, index) => {
      if (ids.has(thread.id)) {
        context.addIssue({
          code: "custom",
          path: ["threads", index, "id"],
          message: "Thread IDs must be unique within a page",
        });
      }
      ids.add(thread.id);
    });
  });

export const threadMessageListResponseSchema = z
  .strictObject({
    status: z.literal("ok"),
    thread: messageThreadSchema,
    messages: z.array(threadMessageSchema).max(THREAD_MESSAGE_PAGE_LIMIT),
    nextCursor: messageCursorSchema.nullable(),
  })
  .superRefine((response, context) => {
    const ids = new Set<string>();
    let previousSequenceNo: number | null = null;
    response.messages.forEach((message, index) => {
      if (message.threadId !== response.thread.id) {
        context.addIssue({
          code: "custom",
          path: ["messages", index, "threadId"],
          message: "Message must belong to the returned thread",
        });
      }
      if (ids.has(message.id)) {
        context.addIssue({
          code: "custom",
          path: ["messages", index, "id"],
          message: "Message IDs must be unique within a page",
        });
      }
      if (previousSequenceNo !== null && message.sequenceNo >= previousSequenceNo) {
        context.addIssue({
          code: "custom",
          path: ["messages", index, "sequenceNo"],
          message: "Messages must be ordered newest first",
        });
      }
      if (message.sequenceNo > response.thread.lastSequenceNo) {
        context.addIssue({
          code: "custom",
          path: ["messages", index, "sequenceNo"],
          message: "Message sequence must not exceed the thread sequence",
        });
      }
      ids.add(message.id);
      previousSequenceNo = message.sequenceNo;
    });
  });

export const sendThreadMessageInputSchema = z.strictObject({
  bodyText: messageBodyTextSchema,
  replyToMessageId: messageIdSchema.nullable(),
});

export const sendThreadMessageResponseSchema = z.strictObject({
  status: z.enum(["sent", "existing"]),
  message: threadMessageSchema,
});

export const markMessageThreadReadInputSchema = z.strictObject({
  throughSequenceNo: sequenceNoSchema,
});

export const markMessageThreadReadResponseSchema = z.strictObject({
  status: z.enum(["read", "existing"]),
  threadId: messageThreadIdSchema,
  lastReadSequenceNo: lastSequenceNoSchema,
  participantVersionNo: versionNoSchema,
  unreadCount: unreadCountSchema,
});

export type MessageParticipantRole = z.infer<typeof messageParticipantRoleSchema>;
export type MessageThread = z.infer<typeof messageThreadSchema>;
export type ThreadMessage = z.infer<typeof threadMessageSchema>;
export type MessageThreadListResponse = z.infer<typeof messageThreadListResponseSchema>;
export type ThreadMessageListResponse = z.infer<typeof threadMessageListResponseSchema>;
export type SendThreadMessageInput = z.input<typeof sendThreadMessageInputSchema>;
export type ParsedSendThreadMessageInput = z.output<typeof sendThreadMessageInputSchema>;
export type SendThreadMessageResponse = z.infer<typeof sendThreadMessageResponseSchema>;
export type MarkMessageThreadReadInput = z.infer<typeof markMessageThreadReadInputSchema>;
export type MarkMessageThreadReadResponse = z.infer<typeof markMessageThreadReadResponseSchema>;
